/**
 * SyncProgressBar - Animated Progress Bar for Sync Status
 *
 * Shows visual progress during data synchronization.
 *
 * @module components/sync/SyncProgressBar
 */

import { cn } from "~/core/utils/cn";

// ============================================================================
// TYPES
// ============================================================================

export interface SyncProgressBarProps {
  /** Progress percentage (0-100) */
  percent: number;
  /** Optional className for customization */
  className?: string;
  /** Whether to show animation (default: true) */
  animated?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

export function SyncProgressBar({
  percent,
  className,
  animated = true,
}: SyncProgressBarProps) {
  // Clamp percent to 0-100
  const clampedPercent = Math.max(0, Math.min(100, percent));

  return (
    <div
      className={cn(
        "w-full h-2 bg-elevated rounded-full overflow-hidden",
        className
      )}
      role="progressbar"
      aria-valuenow={clampedPercent}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div
        className={cn(
          "h-full bg-accent rounded-full",
          animated && "transition-all duration-300 ease-out"
        )}
        style={{ width: `${clampedPercent}%` }}
      />
    </div>
  );
}

export default SyncProgressBar;
